import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { debug, info, warn, error } from "../logger";
import PageHeader from "../components/PageHeader";

type Role = "owner" | "editor" | "viewer" | "admin";
type ListStatus = "active" | "deleted";

interface ListDef {
  id: string;
  name: string;
  userId?: string;
  createdAt?: Date;
  updatedAt?: Date;
  itemCount: number;
  status?: ListStatus;
  roles?: Record<string, Role>;
  memberIds?: string[];
  effectiveRole?: Role | null;
}

export default function ListsList() {
  const [lists, setLists] = useState<ListDef[]>([]);
  const [newName, setNewName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const navigate = useNavigate();

  // ---- Load admin flag ----
  useEffect(() => {
    async function loadRole() {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        const role = snap.exists() ? (snap.data() as any)?.accountRole : undefined;
        setIsAdmin(role === "admin");
      } catch (e) {
        debug("ListsList: loadRole fallback (non-admin).", e);
        setIsAdmin(false);
      }
    }
    loadRole();
  }, []);

  const labelForRole = (r: Role | null | undefined) =>
    r === "owner"
      ? "Owner"
      : r === "editor"
      ? "Editor"
      : r === "viewer"
      ? "Viewer"
      : r === "admin"
      ? "Admin"
      : "";

  const computeEffectiveRole = (row: ListDef, uid: string): Role | null => {
    if (!uid) return null;
    if (row.userId === uid) return "owner";
    if (isAdmin && showAll) return "admin";
    const r = row.roles?.[uid];
    if (r === "editor" || r === "viewer" || r === "owner") return r;
    if (Array.isArray(row.memberIds) && row.memberIds.includes(uid)) return "viewer";
    return null;
  };

  // ---- Fetch lists (owned + delegated) ----
  useEffect(() => {
    async function fetchLists() {
      const user = auth.currentUser;
      if (!user) {
        warn("ListsList: no authenticated user");
        setLists([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setErrMsg(null);

      try {
        let rows: ListDef[] = [];

        if (isAdmin && showAll) {
          // Admin: show all lists
          const snapAll = await getDocs(collection(db, "lists"));
          rows = await Promise.all(
            snapAll.docs.map(async (d) => {
              const v = d.data() as any;
              const createdAt = v.createdAt?.toDate?.();
              const updatedAt = v.updatedAt?.toDate?.();
              const status: ListStatus = v.status ?? "active";
              const itemsSnap = await getDocs(collection(db, "lists", d.id, "items"));
              return {
                id: d.id,
                name: v.name || "(Untitled)",
                userId: v.userId,
                createdAt,
                updatedAt,
                itemCount: itemsSnap.size,
                status,
                roles: v.roles,
                memberIds: v.memberIds,
              };
            })
          );
        } else {
          // Normal user (owned + delegated)
          const uid = user.uid;
          const ownQ = query(collection(db, "lists"), where("userId", "==", uid));
          const ownSnap = await getDocs(ownQ);
          const memberQ = query(
            collection(db, "lists"),
            where("memberIds", "array-contains", uid)
          );
          const memberSnap = await getDocs(memberQ);

          const map = new Map<string, ListDef>();
          const pushDoc = async (d: any) => {
            const v = d.data() as any;
            const createdAt = v.createdAt?.toDate?.();
            const updatedAt = v.updatedAt?.toDate?.();
            const status: ListStatus = v.status ?? "active";
            const itemsSnap = await getDocs(collection(db, "lists", d.id, "items"));
            map.set(d.id, {
              id: d.id,
              name: v.name || "(Untitled)",
              userId: v.userId,
              createdAt,
              updatedAt,
              itemCount: itemsSnap.size,
              status,
              roles: v.roles,
              memberIds: v.memberIds,
            });
          };
          await Promise.all([...ownSnap.docs.map(pushDoc), ...memberSnap.docs.map(pushDoc)]);
          rows = Array.from(map.values());
        }

        // Filter deleted
        const uid = auth.currentUser?.uid || "";
        rows = rows
          .filter((r) => r.status !== "deleted")
          .map((r) => ({ ...r, effectiveRole: computeEffectiveRole(r, uid) }))
          .sort((a, b) => (b.updatedAt?.getTime?.() ?? 0) - (a.updatedAt?.getTime?.() ?? 0));

        setLists(rows);
        info("ListsList: loaded", rows.length, "lists");
      } catch (e: any) {
        error("ListsList: fetchLists failed:", e);
        setErrMsg(e?.message || "Failed to load lists.");
        setLists([]);
      } finally {
        setLoading(false);
      }
    }

    fetchLists();
  }, [isAdmin, showAll]);

  // ---- Create new list ----
  const handleCreate = async () => {
    const name = newName.trim();
    const user = auth.currentUser;
    if (!user || !name) return;
    try {
      const ref = await addDoc(collection(db, "lists"), {
        userId: user.uid,
        name,
        status: "active",
        roles: { [user.uid]: "owner" },
        memberIds: [user.uid],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      navigate(`/lists/${ref.id}/edit`);
    } catch (e) {
      error("ListsList: create failed:", e);
    }
  };

  // ---- Soft delete ----
  const handleDelete = async (id: string) => {
    if (!window.confirm("Move this list to Deleted Items?")) return;
    try {
      await updateDoc(doc(db, "lists", id), {
        status: "deleted",
        updatedAt: serverTimestamp(),
      });
      setLists((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      error("ListsList: delete failed for", id, e);
    }
  };

  const formatDate = (d?: Date) =>
    d
      ? d.toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const columns: GridColDef<ListDef>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "List Name",
        flex: 1,
        renderCell: ({ row }) => (
          <Button
            onClick={() =>
              navigate(
                row.effectiveRole === "viewer"
                  ? `/lists/${row.id}/edit`
                  : `/lists/${row.id}`
              )
            }
            size="small"
            sx={{ textTransform: "none" }}
          >
            {row.name}
          </Button>
        ),
      },
      {
        field: "itemCount",
        headerName: "Items",
        width: 100,
        renderCell: ({ row }) => `${row.itemCount || 0} items`,
      },
      {
        field: "createdAt",
        headerName: "Created",
        width: 180,
        renderCell: ({ row }) => formatDate(row.createdAt),
      },
      {
        field: "updatedAt",
        headerName: "Last Updated",
        width: 180,
        renderCell: ({ row }) => formatDate(row.updatedAt),
      },
      {
        field: "access",
        headerName: "Collaboration",
        width: 150,
        renderCell: ({ row }) => labelForRole(row.effectiveRole),
        sortable: false,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 150,
        getActions: (params) => {
          const role = params.row.effectiveRole;
          const id = params.id as string;

          if (role === "owner" || role === "admin") {
            return [
              <GridActionsCellItem
                key="manage"
                label="Manage"
                onClick={() => navigate(`/lists/${id}`)}
                showInMenu
              />,
              <GridActionsCellItem
                key="edit"
                label="Edit Items"
                onClick={() => navigate(`/lists/${id}/edit`)}
                showInMenu
              />,
              <GridActionsCellItem
                key="delete"
                label="Delete"
                onClick={() => handleDelete(id)}
                showInMenu
              />,
            ];
          }

          if (role === "editor") {
            return [
              <GridActionsCellItem
                key="edit"
                label="Edit Items"
                onClick={() => navigate(`/lists/${id}/edit`)}
                showInMenu
              />,
            ];
          }

          if (role === "viewer") {
            return [
              <GridActionsCellItem
                key="view"
                label="View Items"
                onClick={() => navigate(`/lists/${id}/edit`)}
                showInMenu
              />,
            ];
          }

          return [];
        },
      },
    ],
    [isAdmin, showAll]
  );

  return (
    <Box sx={{ p: 2 }}>
      {errMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errMsg}
        </Alert>
      )}

      <PageHeader
        title="My Lists"
        subtitle="Share answer options across questions"
        actions={
          isAdmin ? (
            <FormControlLabel
              control={<Switch checked={showAll} onChange={(_, v) => setShowAll(v)} />}
              label="Show all (admin)"
            />
          ) : undefined
        }
      />

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
        <TextField
          label="New list name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={handleCreate} disabled={!newName.trim()}>
          Create List
        </Button>
      </Box>

      <Box sx={{ width: "100%", overflowX: "auto", mt: 1 }}>
        <DataGrid
          loading={loading}
          rows={lists}
          columns={columns}
          getRowId={(r) => r.id}
          pageSizeOptions={[5, 10, 20]}
          initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
          pagination
          autoHeight
          sx={{ "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" } }}
        />
      </Box>

      {/* Deleted items button hidden for now */}
      {false && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={() => navigate("/deleted")}>
            View Deleted Items
          </Button>
        </Box>
      )}
    </Box>
  );
}
